import { SelectOption } from "@mui/base";

/**
 * Checks if a variable is a string
 * @param str
 */
export function isString(str: unknown): boolean {
  return typeof str === "string" || str instanceof String;
}

/**
 * Validates email format.
 * @param email
 * @return true if email is in a valid email format.
 */
export function validateEmailFormat(email: string): boolean {
  return !!email.match(
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  );
}

/**
 * Validates multiple emails.
 * @param emails
 * @return true if all emails are valid. Returns false if at least one is invalid.
 */
export function validateEmails(emails: string[]): boolean {
  return emails.every(email => validateEmailFormat(email.trim()));
}

export function normalizePhoneNumberForSubmission(phoneNumber: string): string {
  const trimmedPhoneNumber = phoneNumber.trim();

  if (!trimmedPhoneNumber) {
    return "";
  }

  const normalizedDigits = trimmedPhoneNumber.replace(/\D/g, "");

  return trimmedPhoneNumber.startsWith("+") ? `+${normalizedDigits}` : normalizedDigits;
}

export function validatePhoneNumber(phoneNumber: string): string | undefined {
  const trimmedPhoneNumber = phoneNumber.trim();

  if (!trimmedPhoneNumber) {
    return "Required";
  }

  if (!/^\+\d[\d\s()-]*$/.test(trimmedPhoneNumber)) {
    return "The phone number must start with + and include a country code. Example: +1 (432) 555-1212.";
  }

  if (!/^\+\d+$/.test(normalizePhoneNumberForSubmission(trimmedPhoneNumber))) {
    return "The phone number must start with + and include a country code. Example: +1 (432) 555-1212.";
  }

  return undefined;
}

/**
 * Validates URL format.
 * @param url
 * @return true if URL is in a valid URL format.
 */
export function validateURLFormat(url: string): boolean {
  // Regular expression pattern to match URL format
  const urlPattern = /^(?:\w+:)?\/\/([^\s.]+\.\S{2}|localhost[:?\d]*)\S*$/;
  // Test the URL against the pattern
  return urlPattern.test(url);
}

export const SELECT_OPTION_COMPARATOR = (o1: SelectOption<any>, o2: SelectOption<any>): number =>
  (o1.label as string).localeCompare(o2.label as string);

/**
 * Validates IPv4 address format.
 * @param ip
 * @return true if IP is in a valid IPv4 format.
 */
export function validateIPv4Format(ip: string): boolean {
  const ipv4Regex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
  return ipv4Regex.test(ip);
}

/**
 * Validates IPv6 address format.
 * @param ip
 * @return true if IP is in a valid IPv6 format.
 */
export function validateIPv6Format(ip: string): boolean {
  // Special cases for common IPv6 addresses
  if (ip === "::" || ip === "::1") {
    return true;
  }

  // Must contain at least one colon to be IPv6
  if (!ip.includes(":")) {
    return false;
  }

  // Should not contain dots (that would be IPv4)
  if (ip.includes(".")) {
    return false;
  }

  // IPv6 can have at most one occurrence of "::" (double colon)
  const doubleColonCount = (ip.match(/::/g) || []).length;
  if (doubleColonCount > 1) {
    return false;
  }

  // Basic IPv6 validation - check for colon-separated hex groups
  // This is a simplified validation that covers most practical cases
  const parts = ip.split(":");

  // IPv6 should have at least 3 parts (minimum ::1 has 3 when split)
  if (parts.length < 3 || parts.length > 8) {
    return false;
  }

  // Check if it contains only valid hex characters and colons
  const hexPattern = /^[0-9a-fA-F]*$/;
  for (const part of parts) {
    if (part.length > 4 || (part.length > 0 && !hexPattern.test(part))) {
      return false;
    }
  }

  return true;
}

/**
 * Validates CIDR notation format.
 * @param cidr
 * @return true if CIDR is in a valid format.
 */
export function validateCIDRFormat(cidr: string): boolean {
  const parts = cidr.split("/");
  if (parts.length !== 2) {
    return false;
  }

  const [ip, prefix] = parts;
  const prefixNum = parseInt(prefix, 10);

  // Check if it's IPv4 CIDR
  if (validateIPv4Format(ip)) {
    return prefixNum >= 0 && prefixNum <= 32;
  }

  // Check if it's IPv6 CIDR
  if (validateIPv6Format(ip)) {
    return prefixNum >= 0 && prefixNum <= 128;
  }

  return false;
}

/**
 * Validates IP address or CIDR notation format.
 * @param ipOrCidr
 * @return true if input is a valid IP address or CIDR notation.
 */
export function validateIPOrCIDRFormat(ipOrCidr: string): boolean {
  // Check if it's a CIDR notation
  if (ipOrCidr.includes("/")) {
    return validateCIDRFormat(ipOrCidr);
  }

  // Check if it's an IP address
  return validateIPv4Format(ipOrCidr) || validateIPv6Format(ipOrCidr);
}

/**
 * Validates multiple IP addresses or CIDR notations.
 * @param ipList
 * @return true if all IP addresses/CIDRs are valid. Returns false if at least one is invalid.
 */
export function validateIPOrCIDRList(ipList: string[]): boolean {
  return ipList.every(ip => validateIPOrCIDRFormat(ip.trim()));
}

/**
 * Checks if a string contains HTML code.
 * @param input
 * @return true if the input contains HTML code.
 */
export function containsHTML(input: string): boolean {
  const htmlRegex = /<\/?[a-z][\s\S]*>/i;
  return htmlRegex.test(input);
}
